/**
 * Utility functions for checking account suspension status
 * and showing appropriate error messages to users
 */

import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/authStore';

/**
 * Checks if the account is suspended
 * Blocks ALL suspended users regardless of download access
 * Only allows: Cloud Explorer (anhören), Delete, Download
 */
export async function checkManualSuspension(userId: string): Promise<boolean> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_suspended')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      console.warn('⚠️ Could not check suspension status:', error);
      return false;
    }

    // Account is suspended if is_suspended = true
    // Block ALL features except: Cloud Explorer (anhören), Delete, Download
    const isSuspended = profile.is_suspended === true;
    console.log(`🔍 [checkManualSuspension] User ${userId}: is_suspended=${profile.is_suspended}, result=${isSuspended}`);
    return isSuspended;
  } catch (error) {
    console.error('Error checking manual suspension:', error);
    return false;
  }
}

/**
 * Checks if the current user's account is manually suspended
 * Uses the auth store to get the current user
 */
export async function checkCurrentUserManualSuspension(): Promise<boolean> {
  const { user } = useAuthStore.getState();
  if (!user?.id) {
    return false;
  }
  return checkManualSuspension(user.id);
}

/**
 * Throws an error if the account is manually suspended
 * Use this in functions that should be blocked for suspended accounts
 */
export async function throwIfManuallySuspended(userId?: string): Promise<void> {
  const id = userId || useAuthStore.getState().user?.id;
  if (!id) {
    return;
  }

  const isManuallySuspended = await checkManualSuspension(id);
  if (isManuallySuspended) {
    throw new Error('ACCOUNT_SUSPENDED');
  }
}

/**
 * Error message for suspended accounts
 * Centralized message used everywhere
 */
export const SUSPENSION_ERROR_MESSAGE = {
  title: 'Account Suspended',
  message: "You're suspended",
};

/**
 * Centralized function to show suspension error
 * Use this everywhere for consistent messaging
 */
export function showSuspensionError(
  showToast: (type: 'error' | 'success' | 'warning' | 'info', title: string, message?: string) => void
): void {
  showToast('error', SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
}

/**
 * Shows a toast notification if the account is manually suspended
 * Returns true if account is suspended, false otherwise
 * Never throws - always returns a boolean
 */
export async function showSuspensionToastIfNeeded(
  showToast: (type: 'error' | 'success' | 'warning' | 'info', title: string, message?: string) => void
): Promise<boolean> {
  try {
    const isManuallySuspended = await checkCurrentUserManualSuspension();
    console.log(`🔍 [showSuspensionToastIfNeeded] isManuallySuspended=${isManuallySuspended}`);
    if (isManuallySuspended) {
      console.log('🚫 [showSuspensionToastIfNeeded] User is suspended - blocking action');
      try {
        showSuspensionError(showToast);
      } catch (toastError) {
        console.error('Error showing suspension toast:', toastError);
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error checking suspension status:', error);
    // Don't throw - just return false to allow the operation
    return false;
  }
}

